import Toggle from '@risk-smart/themed-cloudscape-components/toggle';
import Link from '@risksmart-app/components/src/link';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { Edit02 } from '@untitled-ui/icons-react';
import _ from 'lodash';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { BasicModule } from 'src/context/moduleContext';

import { AppetiteCascadingSettings } from './AppetiteCascadingSettings';
import { IngestionConfigSettings } from './IngestionConfigSettings';
import { RiskScoreSettings } from './RiskScoreSettings';

// Maps a submodule key (as defined in `packages/modules/src/defaults.ts`) to the
// `Parent_Type_Enum` that actually owns the configurable tabs in Hasura's `tab`
// table. Most submodule keys do not correspond to a `Parent_Type_Enum` value —
// they describe a *capability* (e.g. `oversight_register_and_findings`), while
// the tab defaults are keyed by the underlying entity
// (`compliance_monitoring_assessment`, set up in migration `1746609743782_tabs`).
//
// Any submodule with `allowTabConfig: true` MUST be added here, otherwise the
// "Edit tabs" link is suppressed (rather than rendering an empty modal — the
// RSP-5774 bug).
const SUBMODULE_KEY_TO_PARENT_TYPE: Record<string, Parent_Type_Enum> = {
  oversight_register_and_findings:
    Parent_Type_Enum.ComplianceMonitoringAssessment,
};

const SubModuleSettings: FC<{
  subModules?: Record<string, BasicModule>;
  toggleItem: (id: string) => void;
  setShowTabSettings: (show: boolean) => void;
  setTabSettingsParentType: (type: Parent_Type_Enum | undefined) => void;
  moduleId: string;
  isDirty?: boolean;
}> = ({
  subModules,
  toggleItem,
  moduleId,
  setShowTabSettings,
  setTabSettingsParentType,
  isDirty,
}) => {
  const { t } = useTranslation('common', { keyPrefix: 'modules' });

  return (
    <>
      <h4>{t('submodules_title')}</h4>
      {_.map(subModules, (subModule: BasicModule, subKey) => {
        const tabParentType = SUBMODULE_KEY_TO_PARENT_TYPE[subKey];

        return (
          <div
            key={subKey}
            className={
              'flex border-0 border-grey150 border-solid border-t-[0.5px] pt-4'
            }
          >
            <div>
              <Toggle
                checked={subModule.enabled}
                onChange={() => toggleItem(`${moduleId}.subModules.${subKey}`)}
                ariaLabel={`Toggle ${subKey}`}
              />
            </div>
            <div className={'ml-[8px]'}>
              <h4 className={'my-0'}>
                {/* @ts-ignore */}
                {t(`titles.${subKey}`)}
              </h4>
              {/* @ts-ignore */}
              <p>{t(`descriptions.${subKey}`)}</p>
              {subKey === 'risk_scoring' && subModule.enabled && (
                <RiskScoreSettings />
              )}
              {subKey === 'appetite_cascading' && subModule.enabled && (
                <AppetiteCascadingSettings />
              )}
              {subKey === 'reg_feed' && subModule.enabled && (
                <IngestionConfigSettings />
              )}
              {subModule.allowTabConfig && tabParentType && !isDirty && (
                <>
                  <h4>{t('configuration')}</h4>
                  <Link
                    onClick={() => {
                      setTabSettingsParentType(tabParentType);
                      setShowTabSettings(true);
                    }}
                  >
                    <div className={'flex items-center gap-2'}>
                      <Edit02 width={16} />
                      <span>{t('edit_tabs')}</span>
                    </div>
                  </Link>
                </>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
};

export default SubModuleSettings;
